import { useRef, useState, type ChangeEvent } from 'react'
import { ImagePlus, Loader2, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { uploadImage, type UploadScope } from '@/lib/uploads'

interface ImageUploadProps {
  readonly value: string | null | undefined
  readonly onChange: (url: string) => void
  readonly scope: UploadScope
  readonly required?: boolean
}

/**
 * Image input with S3-backed upload. Two ways to set a value:
 *   - pick a file → uploaded to S3, publicUrl saved
 *   - paste an existing URL (admin fallback; will be migrated to S3 by seed)
 */
export function ImageUpload({ value, onChange, scope, required }: ImageUploadProps) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const fileRef = useRef<HTMLInputElement>(null)

  async function handlePick(e: ChangeEvent<HTMLInputElement>): Promise<void> {
    const file = e.target.files?.[0]
    if (!file) return
    setBusy(true)
    setError(null)
    try {
      const url = await uploadImage(file, scope)
      onChange(url)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload échoué')
    } finally {
      setBusy(false)
      if (fileRef.current) fileRef.current.value = ''
    }
  }

  return (
    <div className="space-y-2">
      {value ? (
        <div className="relative inline-block">
          <img
            src={value}
            alt=""
            className="h-32 w-48 rounded-md border border-border object-cover"
            onError={(e) => {
              e.currentTarget.style.opacity = '0.3'
            }}
          />
          <button
            type="button"
            aria-label="Retirer l'image"
            onClick={() => {
              onChange('')
            }}
            className="absolute -right-2 -top-2 rounded-full bg-destructive p-1 text-destructive-foreground shadow"
          >
            <X className="h-3 w-3" />
          </button>
        </div>
      ) : (
        <div className="flex h-32 w-48 items-center justify-center rounded-md border-2 border-dashed border-border text-muted-foreground">
          <ImagePlus className="h-6 w-6" />
        </div>
      )}

      <div className="flex items-center gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={busy}
          onClick={() => fileRef.current?.click()}
        >
          {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          {busy ? 'Upload…' : value ? 'Remplacer' : 'Choisir un fichier'}
        </Button>
        <input
          ref={fileRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/avif"
          className="hidden"
          onChange={(e) => {
            void handlePick(e)
          }}
        />
      </div>

      <Input
        type="url"
        placeholder="ou colle une URL d'image"
        value={value ?? ''}
        onChange={(e) => {
          onChange(e.target.value)
        }}
        required={required}
      />

      {error ? <p className="text-xs text-destructive">{error}</p> : null}
    </div>
  )
}
